<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import whatsappCallAPI from 'dashboard/api/whatsappCall';

// ============================================================================
// v109 (WhatsApp Call — Fase 4): CONFIGURAÇÃO — CHAVE GERAL (liga/desliga o
// sistema INTEIRO de ligações) + toggles POR USUÁRIO (desativado = sem botão,
// não recebe ligação, não vê funcionalidades). Escrita: SÓ o id 1 (backend
// valida; a tela mostra aviso pros demais admins).
// ============================================================================

const route = useRoute();
const accountId = computed(() => route.params.accountId);

const isLoading = ref(false);
const salvando = ref('');
const error = ref('');
const sistemaAtivo = ref(false);
const usuarios = ref([]);
const busca = ref('');

const usuariosFiltrados = computed(() => {
  const q = busca.value.trim().toLowerCase();
  if (!q) return usuarios.value;
  return usuarios.value.filter(u => u.nome.toLowerCase().includes(q));
});

const load = async () => {
  isLoading.value = true;
  error.value = '';
  try {
    const { data } = await whatsappCallAPI.config(accountId.value);
    sistemaAtivo.value = data.sistema_ativo;
    usuarios.value = data.usuarios || [];
  } catch (e) {
    error.value = e.response?.data?.error || e.message;
  } finally {
    isLoading.value = false;
  }
};

const alternarChaveGeral = async () => {
  salvando.value = 'geral';
  try {
    const { data } = await whatsappCallAPI.setChaveGeral(accountId.value, !sistemaAtivo.value);
    sistemaAtivo.value = data.sistema_ativo;
    useAlert(sistemaAtivo.value ? '📞 Sistema de ligações LIGADO' : '🔌 Sistema de ligações DESLIGADO');
  } catch (e) {
    useAlert(e.response?.status === 403 ? 'Apenas o administrador principal pode usar a chave geral.' : `Erro: ${e.message}`);
  } finally {
    salvando.value = '';
  }
};

const alternarUsuario = async u => {
  salvando.value = `u${u.id}`;
  try {
    const { data } = await whatsappCallAPI.setUsuario(accountId.value, u.id, !u.ativo);
    u.ativo = data.ativo;
    useAlert(`${u.nome}: ligações ${u.ativo ? 'ATIVADAS' : 'desativadas'}`);
  } catch (e) {
    useAlert(e.response?.status === 403 ? 'Apenas o administrador principal pode alterar.' : `Erro: ${e.message}`);
  } finally {
    salvando.value = '';
  }
};

onMounted(load);
</script>

<!-- eslint-disable vue/no-bare-strings-in-template -->
<template>
  <div class="overflow-auto p-4 w-full h-full bg-n-background">
    <div class="flex gap-3 items-center mb-5">
      <span class="wc-ico"><span class="i-lucide-settings size-5" /></span>
      <div>
        <h1 class="text-xl font-semibold text-n-slate-12">WhatsApp Call · Configuração</h1>
        <p class="text-xs text-n-slate-11">Chave geral do sistema e permissão por usuário</p>
      </div>
    </div>

    <p v-if="error" class="p-3 mb-4 text-sm rounded-lg border border-n-ruby-7 bg-n-ruby-2 text-n-ruby-11">{{ error }}</p>

    <div v-if="isLoading" class="py-12 text-center text-n-slate-11">
      <span class="inline-block i-lucide-loader-circle animate-spin size-7" />
    </div>

    <template v-else>
      <!-- CHAVE GERAL -->
      <div class="wc-chave" :class="sistemaAtivo ? 'on' : 'off'">
        <div class="flex-1">
          <p class="text-sm font-bold text-n-slate-12">
            Chave geral do sistema de ligações
          </p>
          <p class="mt-1 text-xs text-n-slate-11">
            {{ sistemaAtivo
              ? 'LIGADO — o número recebe/faz ligações e os popups aparecem pros usuários ativados.'
              : 'DESLIGADO — nenhum evento é processado (custo ~zero no servidor), nada toca, nada aparece.' }}
          </p>
        </div>
        <button
          type="button"
          class="wc-switch"
          :class="{ on: sistemaAtivo }"
          :disabled="salvando === 'geral'"
          @click="alternarChaveGeral"
        >
          <span class="wc-switch-bola" />
          <b>{{ sistemaAtivo ? 'LIGADO' : 'DESLIGADO' }}</b>
        </button>
      </div>

      <!-- POR USUÁRIO -->
      <div class="flex gap-2 items-center mt-6 mb-3">
        <h2 class="text-sm font-semibold text-n-slate-12">Ligações por usuário</h2>
        <span class="text-[11px] text-n-slate-11">— desativado não tem botão, não recebe e não vê nada de ligação</span>
        <span class="flex-1" />
        <input
          v-model="busca"
          placeholder="Buscar usuário…"
          class="px-3 py-1.5 w-56 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12"
        />
      </div>
      <div class="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
        <div v-for="u in usuariosFiltrados" :key="u.id" class="wc-user">
          <div class="flex-1 min-w-0">
            <p class="text-sm font-semibold truncate text-n-slate-12">{{ u.nome }}</p>
            <p class="text-[11px] text-n-slate-11">{{ u.role }}</p>
          </div>
          <button
            type="button"
            class="wc-switch mini"
            :class="{ on: u.ativo }"
            :disabled="salvando === `u${u.id}`"
            @click="alternarUsuario(u)"
          >
            <span class="wc-switch-bola" />
          </button>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.wc-ico {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: linear-gradient(135deg, #25d366, #128c7e);
  color: #06301a;
}
.wc-chave {
  display: flex;
  gap: 16px;
  align-items: center;
  padding: 18px;
  border-radius: 16px;
  border: 2px solid;
  background: linear-gradient(180deg, #17181d, #111216);
}
.wc-chave.on { border-color: #25d366; box-shadow: 0 0 0 2px rgba(37, 211, 102, 0.2); }
.wc-chave.off { border-color: #3a3d45; }
.wc-switch {
  display: inline-flex;
  gap: 10px;
  align-items: center;
  padding: 8px 14px 8px 8px;
  border: none;
  border-radius: 999px;
  background: #3a3d45;
  color: #cbd5e1;
  font-size: 12px;
  cursor: pointer;
  transition: background 0.15s ease;
}
.wc-switch.on { background: #25d366; color: #06301a; }
.wc-switch-bola {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #fff;
  transition: transform 0.15s ease;
}
.wc-switch.on .wc-switch-bola { transform: translateX(2px); }
.wc-switch.mini { padding: 4px; }
.wc-switch.mini .wc-switch-bola { width: 16px; height: 16px; }
.wc-user {
  display: flex;
  gap: 10px;
  align-items: center;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.03);
}
</style>
